import { type MbscCalendarEvent } from '@mobiscroll/react';
import { v7 } from 'uuid';

import { type Ablauf, type AkaVeranstaltungId, type Geschaeftsbereich } from '../../../../../dtos';
import { type AbstractCalendarResource } from '../CalendarResource';
import { type CalendarResourceType } from '../calendarResourceTypes';
import { type CalendarEventState, calendarEventStates } from './calendarEventStates';

export type BlockungData = {
  /**
   * This is either a Kuerzel from an Ort, or a Standort.
   */
  locationKuerzel?: string | null;

  raumgroesse?: number | null;

  /**
   * This field represents the ID of the Veranstaltung/Termin that this Blockung belongs to, as written in the system that created this Blockung (for example, SAP).
   * This is equal to the akaVeranstaltungId if the Blockung was created by us.
   *
   * StandortBlockungen can only come from our system, so it's taken from StandortBlockung -> Veranstaltung -> akaVeranstaltungId.
   *
   * RaumBlockungen and ExperteBlockungen can come from other systems than ours.
   *
   * For ExperteBlockungen, this is called 'quelleTerminId' in the database. As of now, this is equal to the Leistung/Teilleistung-ID, so not comparable to akaVeranstaltungId
   * For RaumBlockungen, this is called 'akaVeranstaltungId' in the database.
   */
  quelleTerminId: string;

  /**
   * This is some data of the Veranstaltung that this Blockung belongs to, exactly as it is in the database.
   * Can be null if the Blockung came from an external system.
   */
  veranstaltung: {
    akaVeranstaltungId: AkaVeranstaltungId;
    geschaeftsbereich: Geschaeftsbereich;
    id: number;
    thema: {
      buchungsnummer: string;
      produktSapId: number;
    };
    titel: string;
    veranstaltungSapId: number | null;
  } | null;
  /**
   * This describes the zeitraeume in which the Ressource is blocked (exactly as in database). Each entry in the array is one day.
   */
  zeitraeume: Ablauf;
};

/**
 * Represents one Blockung of a Ressource in our system (either Raumblockung or Experteblockung or Standortblockung), and one event in the calendar.
 */
export abstract class AbstractCalendarEvent implements MbscCalendarEvent {
  /**
   * Type of the Blockung.
   */
  public abstract readonly type: CalendarResourceType;

  /**
   * Data of the Blockung - depending on the type, additional data might be stored here.
   */
  public abstract readonly blockungData: BlockungData;

  /**
   * This is the id of the CalendarResource that created this event.
   * See AbstractCalendarResource for more information.
   * Needed by Mobiscroll -> Don't rename.
   */
  public readonly resource: string;

  /**
   * Unique event identifier.
   * Needed by Mobiscroll -> Don't rename.
   */
  public readonly id: string = v7();

  /**
   * This is the sapId of the Ressource of the Blockung.
   */
  public ressourceSapId: number;

  /**
   * Events should be displayed with the correct times
   */
  public allDay = false;

  /**
   * Start of the Blockung.
   * Needed by Mobiscroll -> Don't rename.
   */
  public start: Date;

  /**
   * End of the Blockung.
   * Needed by Mobiscroll -> Don't rename.
   */
  public end: Date;

  public isCurrentSelection = false;

  public isOldSelection = false;

  /**
   * Indicates whether this event has a state that should be visually highlighted in the calendar.
   * This is used to indicate errors, warnings or information about the event.
   **/
  public state: CalendarEventState = calendarEventStates.DEFAULT;

  /**
   * Message that is displayed in the calendar event's tooltip when state is not 'default'.
   */
  public message?: string;

  public editable = false;

  public resize?: boolean = true;

  public dragInTime?: boolean = true;

  public constructor(start: Date, end: Date, calendarResource: AbstractCalendarResource) {
    this.resource = calendarResource.id;
    this.ressourceSapId = calendarResource.getRessourceSapId();
    this.start = start;
    this.end = end;
    this.allDay = calendarResource.type !== 'experte';
  }
}
